import { environment } from 'src/environments/environment'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { Component, OnInit } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { Observable, of } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  take,
  timeout
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { ConfigurationOption } from '../configuration-option'
import { configurationOptionActions } from '../configuration-option.actions'
import { configurationOptionSelectors } from '../configuration-option.selectors'
import { ConfigurationOptionService } from '../configuration-option.service'
import type { LanguageCode } from 'src/app/shared/components/language-inputs/show.component'

const ORGANIZATION_LANGUAGE_CODES: LanguageCode[] = ['nl', 'en', 'fr', 'de']

@Component({
  selector: 'configuration-option-organization-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class ConfigurationOptionOrganizationTabComponent implements OnInit {
  public optionValue$: Observable<any>
  public configOption$: Observable<ConfigurationOption>
  public configFormValues$: Observable<any>
  public regCategoryFilters$: Observable<any>
  public regCategoryOptions$: Observable<any>
  public isCollapsed = false
  public isCollapsedDenominations = true
  public accessRights = AccessRights
  public readonly languageCodes = ORGANIZATION_LANGUAGE_CODES.filter(
    (language) =>
      environment.frontendLanguages.some(
        (frontendLanguage) => frontendLanguage === language
      )
  )
  public availableLanguages = environment.frontendLanguages.reduce(
    (acc, curr) => {
      if (curr != 'xx') return (acc[curr] = true), acc
      else return acc
    },
    {}
  )

  public form: UntypedFormGroup
  public formOrganizationDenominations: UntypedFormGroup

  public get additionalDescriptionFormGroup(): UntypedFormGroup | null {
    const group = this.form?.get('additionalDescription')

    return group instanceof UntypedFormGroup ? group : null
  }

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private notificationService: NotificationService,
    private actions$: Actions,
    private configOptionService: ConfigurationOptionService
  ) {}

  ngOnInit() {
    this.configFormValues$ = this.store
      .select(configurationOptionSelectors.selectAll)
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
        map((configOptions) => {
          return this.configOptionService.mapEntitiesToObject(configOptions)
        })
      )

    this.configOption$ = this.store.select(
      configurationOptionSelectors.selectByKey,
      'organisation_form_config'
    )

    this.optionValue$ = this.configOption$.pipe(
      filter((x) => Boolean(x)),
      map((option) =>
        JSON.parse(option.value ?? '[]').reduce(
          (formData, inputGroup) => ({
            ...formData,
            [inputGroup.inputName]: inputGroup
          }),
          {}
        )
      )
    )

    this.form = this.fb.group({
      description: this.fb.group({
        title_en: ['Description', []],
        title_nl: ['Omschrijving', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Description', []],
        title_de: ['Description', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      additionalDescription: this.fb.group({
        title_en: ['Secondary description', []],
        title_nl: ['Tweede omschrijving', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Description secondaire', []],
        title_de: ['Zusätzliche Beschreibung', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [false, []],
        isEditable: [true, []]
      }),
      abbreviation: this.fb.group({
        title_en: ['Abbreviation', []],
        title_nl: ['Afkorting', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Description', []],
        title_de: ['Description', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      street: this.fb.group({
        title_en: ['Street', []],
        title_nl: ['Straat', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Street', []],
        title_de: ['Street', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      streetNumber: this.fb.group({
        title_en: ['House number', []],
        title_nl: ['Huisnummer', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['House number', []],
        title_de: ['House number', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      postbox: this.fb.group({
        title_en: ['Postbox', []],
        title_nl: ['Postbus', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Postbox', []],
        title_de: ['Postbox', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      city: this.fb.group({
        title_en: ['City', []],
        title_nl: ['Stad', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['City', []],
        title_de: ['City', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      zipCode: this.fb.group({
        title_en: ['Zipcode', []],
        title_nl: ['Postcode', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Zipcode', []],
        title_de: ['Zipcode', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      country: this.fb.group({
        title_en: ['Country', []],
        title_nl: ['Land', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Country', []],
        title_de: ['Country', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      type: this.fb.group({
        title_en: ['Type', []],
        title_nl: ['Type', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Type', []],
        title_de: ['Type', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      parentOrganisationId: this.fb.group({
        title_en: ['Parent', []],
        title_nl: ['Moederaccount', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Parent', []],
        title_de: ['Parent', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      commercialCode: this.fb.group({
        title_en: ['Commercial code', []],
        title_nl: ['Ondernemingsnummer', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Commercial code', []],
        title_de: ['Commercial code', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      website: this.fb.group({
        title_en: ['Website', []],
        title_nl: ['Website', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Website', []],
        title_de: ['Website', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      organisationCode: this.fb.group({
        title_en: ['VAT number', []],
        title_nl: ['BTW nummer', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['VAT number', []],
        title_de: ['VAT number', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      organisationSecondaryCode: this.fb.group({
        title_en: ['Institution number for educational organisation', []],
        title_nl: ['Instellingsnummer voor onderwijsorganisatie', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Institution number for educational organisation', []],
        title_de: ['Institution number for educational organisation', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      phone: this.fb.group({
        title_en: ['Phone', []],
        title_nl: ['Telefoon', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Phone', []],
        title_de: ['Phone', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      email: this.fb.group({
        title_en: ['Email', []],
        title_nl: ['Email', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Email', []],
        title_de: ['Email', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [true, []],
        isEditable: [true, []]
      }),
      tertiaryCode: this.fb.group({
        title_en: ['Tertiary code', []],
        title_nl: ['Tertiary code', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Tertiary code', []],
        title_de: ['Tertiary code', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [false, []],
        isEditable: [true, []]
      }),
      quaternaryCode: this.fb.group({
        title_en: ['Quaternary code', []],
        title_nl: ['Quaternary code', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Quaternary code', []],
        title_de: ['Quaternary code', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [false, []],
        isEditable: [true, []]
      }),
      quintaryCode: this.fb.group({
        title_en: ['Quintary code', []],
        title_nl: ['Quintary code', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Quintary code', []],
        title_de: ['Quintary code', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [false, []],
        isEditable: [true, []]
      }),
      invoiceOrganizationId: this.fb.group({
        title_en: ['Invoice organisation', []],
        title_nl: ['Invoice organisation', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Invoice organisation', []],
        title_de: ['Invoice organisation', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [false, []],
        isEditable: [true, []]
      }),
      category: this.fb.group({
        title_en: ['Category', []],
        title_nl: ['Category', []],
        placeholder_nl: ['', []],
        placeholder_en: ['', []],
        title_fr: ['Category', []],
        title_de: ['Category', []],
        placeholder_fr: ['', []],
        placeholder_de: ['', []],
        isVisible: [true, []],
        isRequired: [false, []],
        isEditable: [true, []]
      })
    })
    this.formOrganizationDenominations = this.fb.group({
      organisationDisplayNameNl: [],
      organisationDisplayNameEn: [],
      organisationsDisplayNameNl: [],
      organisationsDisplayNameEn: [],
      organisationDisplayNameFr: [],
      organisationDisplayNameDe: [],
      organisationsDisplayNameFr: [],
      organisationsDisplayNameDe: []
    })

    this.configFormValues$
      .pipe(
        filter(
          (formValues) =>
            formValues?.organisation_display_name &&
            formValues?.organisations_display_name
        ),
        take(1)
      )
      .subscribe((formValues) => {
        if (
          formValues?.organisation_display_name &&
          formValues?.organisations_display_name
        ) {
          const organisationValues = JSON.parse(
            formValues.organisation_display_name
          )
          const organisationsValues = JSON.parse(
            formValues.organisations_display_name
          )
          this.formOrganizationDenominations?.patchValue({
            organisationDisplayNameNl:
              organisationValues.organisation_display_name_nl,
            organisationDisplayNameEn:
              organisationValues.organisation_display_name_en,
            organisationsDisplayNameNl:
              organisationsValues.organisations_display_name_nl,
            organisationsDisplayNameEn:
              organisationsValues.organisations_display_name_en,
            organisationDisplayNameFr:
              organisationValues.organisation_display_name_fr,
            organisationDisplayNameDe:
              organisationValues.organisation_display_name_de,
            organisationsDisplayNameFr:
              organisationsValues.organisations_display_name_fr,
            organisationsDisplayNameDe:
              organisationsValues.organisations_display_name_de
          })
        }
      })
  }

  public onVisibleInputChange(formFieldName: string) {
    if (!this.form.value[formFieldName]?.isVisible) {
      this.form.controls[formFieldName]['controls']['isRequired'].patchValue(
        false
      )
      this.form.controls[formFieldName]['controls']['isEditable'].patchValue(
        false
      )
    }
  }

  public onSubmitOrganization(form): void {
    if (form.value['organisationDisplayNameNl']) {
      const {
        organisationDisplayNameNl,
        organisationDisplayNameEn,
        organisationsDisplayNameNl,
        organisationsDisplayNameEn,
        organisationDisplayNameFr,
        organisationDisplayNameDe,
        organisationsDisplayNameFr,
        organisationsDisplayNameDe
      } = form.controls

      const formValues = form.value

      formValues['organisation_display_name'] = JSON.stringify({
        organisation_display_name_nl: organisationDisplayNameNl.value,
        organisation_display_name_en: organisationDisplayNameEn.value,
        organisation_display_name_fr: organisationDisplayNameFr.value,
        organisation_display_name_de: organisationDisplayNameDe.value
      })

      formValues['organisations_display_name'] = JSON.stringify({
        organisations_display_name_nl: organisationsDisplayNameNl.value,
        organisations_display_name_en: organisationsDisplayNameEn.value,
        organisations_display_name_fr: organisationsDisplayNameFr.value,
        organisations_display_name_de: organisationsDisplayNameDe.value
      })

      delete formValues.organisation_display_name_nl
      delete formValues.organisation_display_name_en
      delete formValues.organisation_display_name_de
      delete formValues.organisation_display_name_fr
      delete formValues.organisations_display_name_nl
      delete formValues.organisations_display_name_en
      delete formValues.organisations_display_name_de
      delete formValues.organisations_display_name_fr

      this.configOptionService.updateOrCreateMany(formValues)
    } else {
      this.configOption$.pipe(take(1)).subscribe((configOption) => {
        const configOptionValue = JSON.stringify(
          Object.keys(form.value).map((key) => {
            if (!form.value[key]['isVisible']) {
              form.value[key]['isRequired'] = false
              form.value[key]['isEditable'] = false
            }
            return {
              ...form.value[key],
              inputName: key
            }
          })
        )

        if (!configOption) {
          this.store.dispatch(
            configurationOptionActions.createOne({
              entity: {
                key: 'organisation_form_config',
                value: configOptionValue
              }
            })
          )
        } else {
          this.store.dispatch(
            configurationOptionActions.updateOneById({
              id: configOption.id,
              params: {
                value: configOptionValue
              }
            })
          )
        }
      })
    }

    this.actions$
      .pipe(
        ofType(configurationOptionActions.upsertMany),
        take(1),
        timeout(1000),
        catchError((error) => of(null))
      )
      .subscribe((action) =>
        action
          ? this.notificationService.emit('alerts.success', 'success')
          : null
      )
  }
}
